/**
 * Eigentümer-Portal — Eigentümerversammlungen (WEG Phase 5).
 *
 * Zeigt kommende und vergangene Versammlungen aller WEG-Objekte, in denen der
 * Eigentümer mindestens eine Einheit hält. Bietet direkten Download von
 * Einladungs-PDF und Protokoll-PDF via authenticated Bearer-Blob.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";
import {
  Users, CalendarDots, MapPin, Envelope, FileText,
  CheckCircle, Warning, Clock, ArrowRight,
} from "@phosphor-icons/react";

const STATUS_META = {
  planned:   { label: "Geplant",     color: "bg-slate-100 text-slate-700" },
  invited:   { label: "Eingeladen",  color: "bg-blue-100 text-blue-800" },
  held:      { label: "Abgehalten",  color: "bg-amber-100 text-amber-800" },
  closed:    { label: "Abgeschlossen", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Abgesagt",    color: "bg-rose-100 text-rose-800" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

function AssemblyCard({ a, isPast }) {
  const st = STATUS_META[a.status] || STATUS_META.planned;
  const hasMinutes = a.status === "closed";
  const canInvite = ["invited", "held", "closed"].includes(a.status);
  const agendaCount = (a.agenda_items || []).length;
  return (
    <article
      className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden"
      data-testid={`owner-assembly-${a.assembly_number}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-mono">
              {a.property?.property_number || "—"} · Nr. {a.assembly_number}
            </div>
            <h3 className="font-semibold text-lg text-slate-900 mt-1 leading-tight">
              {a.title}
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">{a.property?.name}</div>
          </div>
          <Badge className={`${st.color} text-[10px]`}>{st.label}</Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
              <CalendarDots size={11} className="inline mr-0.5" /> Datum
            </div>
            <div className="text-slate-900 font-medium">
              {fmtDate(a.assembly_date)}
              {a.start_time ? ` · ${a.start_time} Uhr` : ""}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
              <MapPin size={11} className="inline mr-0.5" /> Ort
            </div>
            <div className="text-slate-700">{a.location || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
              <FileText size={11} className="inline mr-0.5" /> Tagesordnung
            </div>
            <div className="text-slate-700">{agendaCount} TOP{agendaCount === 1 ? "" : "s"}</div>
          </div>
        </div>

        {!isPast && a.invited_at && (
          <div className="mt-4 flex items-center gap-2 text-xs">
            {a.invitation_deadline_met === true && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-medium">
                <CheckCircle size={11} weight="fill" /> Ladungsfrist gewahrt ({a.invitation_days} Tage)
              </span>
            )}
            {a.invitation_deadline_met === false && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-medium">
                <Warning size={11} weight="fill" /> Ladungsfrist unterschritten ({a.invitation_days} Tage &lt; 21)
              </span>
            )}
          </div>
        )}

        {/* Tagesordnung Vorschau */}
        {agendaCount > 0 && (
          <details className="mt-4">
            <summary
              className="cursor-pointer text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid={`owner-assembly-agenda-toggle-${a.assembly_number}`}
            >
              Tagesordnung anzeigen <ArrowRight size={11} />
            </summary>
            <ol className="mt-3 space-y-2 text-sm">
              {(a.agenda_items || []).map((it, idx) => (
                <li key={idx} className="border-l-2 border-slate-200 pl-3">
                  <div className="font-medium text-slate-900">
                    {it.number ? <span className="font-mono text-slate-500 mr-1">{it.number}</span> : null}
                    {it.title}
                  </div>
                  {it.proposal_text && (
                    <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{it.proposal_text}</div>
                  )}
                  {isPast && it.outcome && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Abstimmung · Ja {it.votes_yes ?? 0} · Nein {it.votes_no ?? 0} · Enth. {it.votes_abstain ?? 0} ·{" "}
                      <b className={
                        it.outcome === "angenommen" ? "text-emerald-700" :
                        it.outcome === "abgelehnt"  ? "text-rose-700" : "text-amber-700"
                      }>
                        {it.outcome}
                      </b>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </details>
        )}

        {/* PDF-Downloads */}
        <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap gap-2">
          {canInvite && (
            <button
              onClick={() => openAuthenticatedPdf(`/weg/pdf/assembly/${a.id}/invitation`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-medium transition-colors"
              data-testid={`owner-assembly-invitation-${a.assembly_number}`}
            >
              <Envelope size={13} /> Einladung als PDF
            </button>
          )}
          {hasMinutes && (
            <button
              onClick={() => openAuthenticatedPdf(`/weg/pdf/assembly/${a.id}/minutes`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-medium transition-colors"
              data-testid={`owner-assembly-minutes-${a.assembly_number}`}
            >
              <FileText size={13} /> Protokoll als PDF
            </button>
          )}
          {!canInvite && !hasMinutes && (
            <span className="text-[11px] text-slate-400 italic">Einladung wird nach Versand zum Download bereitgestellt.</span>
          )}
        </div>
      </div>
    </article>
  );
}

export default function OwnerVersammlungen() {
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/weg/portal/my-assemblies")
      .then((r) => setData(r.data || { upcoming: [], past: [] }))
      .finally(() => setLoading(false));
  }, []);

  const hasAny = data.upcoming.length > 0 || data.past.length > 0;
  const upcomingSorted = useMemo(
    () => [...data.upcoming].sort((a, b) => (a.assembly_date || "").localeCompare(b.assembly_date || "")),
    [data.upcoming],
  );
  const pastSorted = useMemo(
    () => [...data.past].sort((a, b) => (b.assembly_date || "").localeCompare(a.assembly_date || "")),
    [data.past],
  );

  return (
    <div className="space-y-8" data-testid="owner-versammlungen-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Users weight="duotone" className="text-emerald-700" /> Eigentümerversammlungen
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Kommende und vergangene Versammlungen Ihrer Eigentümergemeinschaften — inklusive
          Download der Einladung und des Protokolls.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : !hasAny ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center bg-slate-50">
          <Users size={44} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-600">
            Aktuell sind keine Versammlungen für Ihre Objekte geplant.
          </div>
        </div>
      ) : (
        <>
          {/* Kommende Versammlungen */}
          <section className="space-y-4" data-testid="owner-assemblies-upcoming">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900 flex items-center gap-2">
                <Clock size={18} className="text-blue-700" /> Kommende Versammlungen
              </h2>
              <span className="text-xs text-slate-500 tabular-nums">
                {upcomingSorted.length} Termin{upcomingSorted.length === 1 ? "" : "e"}
              </span>
            </div>
            {upcomingSorted.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 text-center">
                Keine anstehenden Versammlungen.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {upcomingSorted.map((a) => (
                  <AssemblyCard key={a.id} a={a} isPast={false} />
                ))}
              </div>
            )}
          </section>

          {/* Vergangene Versammlungen */}
          {pastSorted.length > 0 && (
            <section className="space-y-4" data-testid="owner-assemblies-past">
              <div className="flex items-baseline justify-between">
                <h2 className="text-lg font-semibold tracking-tight text-slate-900 flex items-center gap-2">
                  <FileText size={18} className="text-slate-600" /> Vergangene Versammlungen
                </h2>
                <span className="text-xs text-slate-500 tabular-nums">
                  {pastSorted.length} Termin{pastSorted.length === 1 ? "" : "e"}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pastSorted.map((a) => (
                  <AssemblyCard key={a.id} a={a} isPast={true} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
